import { useEffect, useState } from "react";
import { isMainlandChina } from "../../utils/region";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

// Every payment goes through a Stripe-hosted Checkout page: this starts a
// session for the chosen plan and redirects there. Which methods that page
// offers depends on region — mainland China gets WeChat Pay + Alipay,
// everyone else gets card / Apple Pay / Google Pay (see utils/region.js and
// the backend's stripe.js). Stripe sends the buyer back to
// /myfiles?checkout_session=..., where MyFiles.jsx confirms the payment.
function CheckoutButton({ plan, region, token, onError }) {
  const [redirecting, setRedirecting] = useState(false);
  const isChina = region === "cn";

  const startCheckout = async () => {
    setRedirecting(true);
    try {
      const res = await fetch(`${API_BASE_URL}/api/upgrade/checkout/create-session`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ planId: plan.id, region }),
      });
      const data = await res.json();
      // data.error carries Stripe's own reason (e.g. a payment method not
      // enabled in the dashboard) — far more useful than the generic message.
      if (!res.ok || !data.url) {
        throw new Error([data.message || "Failed to start checkout", data.error].filter(Boolean).join(": "));
      }
      window.location.assign(data.url);
    } catch (err) {
      setRedirecting(false);
      onError(err.message || "Failed to start checkout");
    }
  };

  return (
    <button
      type="button"
      onClick={startCheckout}
      disabled={redirecting}
      className={`mt-2 w-full font-semibold px-3 py-2 rounded transition disabled:opacity-60 ${
        isChina ? "bg-[#07C160] hover:bg-[#06AD56] text-white" : "bg-primary hover:bg-primary-dark text-dark-bg"
      }`}
    >
      {redirecting
        ? (isChina ? "正在跳转… Redirecting…" : "Redirecting…")
        : (isChina ? "微信支付 / 支付宝 WeChat Pay / Alipay" : "Pay with card / Apple Pay / Google Pay")}
    </button>
  );
}

export default function UpgradeModal({ token, onClose, mandatory = false }) {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [selectedPlan, setSelectedPlan] = useState(null);
  // false until the backend has Stripe configured.
  const [paymentsEnabled, setPaymentsEnabled] = useState(false);
  // What Plan.price is charged in — from the backend, so the label always
  // matches the actual charge.
  const [currency, setCurrency] = useState("CAD");
  const region = isMainlandChina() ? "cn" : "intl";

  useEffect(() => {
    fetch(`${API_BASE_URL}/api/upgrade/options`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(res => {
        if (!res.ok) throw new Error("Failed to load plans");
        return res.json();
      })
      .then(data => {
        setPlans(data.plans || []);
        setPaymentsEnabled(!!data.paymentsEnabled);
        if (data.currency) setCurrency(data.currency);
      })
      .catch(() => setError("Couldn't load plans. Please try again."))
      .finally(() => setLoading(false));
  }, [token]);

  return (
    <div className="fixed inset-0 bg-black/70 flex justify-center items-center">
      <div className="bg-dark-card p-6 rounded-xl w-96 border border-dark-border shadow-xl">
        <h3 className="mb-4 text-xl font-semibold">{mandatory ? "Choose a Plan to Get Started" : "Upgrade Plan"}</h3>
        {mandatory && (
          <p className="text-sm text-gray-400 mb-4">
            Select and pay for a plan to activate your vault — uploads are locked until you do.
          </p>
        )}

        {loading && <div className="text-sm text-gray-400 mb-3">Loading plans…</div>}
        {error && <div className="text-sm text-red-400 mb-3">{error}</div>}

        {plans.map(p => (
          <div key={p.id} className="border border-dark-border p-3 mb-2 rounded bg-dark-bg">
            <button
              type="button"
              onClick={() => setSelectedPlan(p)}
              className="w-full text-left"
            >
              <div className="font-semibold">{p.name}</div>
              <div className="text-sm text-gray-400">
                {p.maxFiles >= 999999 ? "Unlimited files per folder" : `${p.maxFiles} files per folder`}
              </div>
              <div className="text-primary font-bold">${p.price} {currency}</div>
            </button>

            {selectedPlan?.id === p.id && (paymentsEnabled ? (
              <CheckoutButton key={p.id} plan={p} region={region} token={token} onError={setError} />
            ) : (
              <div className="text-xs text-yellow-500 mt-2">Payment isn't configured yet.</div>
            ))}
          </div>
        ))}

        {!mandatory && (
          <button onClick={onClose} className="mt-4 w-full bg-dark-bg hover:bg-dark-border border border-dark-border px-3 py-2 rounded font-medium transition">
            Close
          </button>
        )}
      </div>
    </div>
  );
}
